import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { People, Search } from '@local/client-contracts';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EventInfo, EventsService } from '@shared/services';
import { windowSizeObserver } from '@shared/utils';
import { HierarchyItem } from 'src/app/bar/components/hierarchy/models/hierarchyItem';
import { ComponentFocused } from 'src/app/bar/models/component-focused.model';
import { PreviewFocus } from 'src/app/bar/models/preview-focus';
import { HubService } from 'src/app/bar/services/hub.service';
import { PreviewKeyboardService } from 'src/app/bar/services/preview-keyboard.service';
import { PreviewService } from 'src/app/bar/services/preview.service';
import { fade } from '../../../../../shared/animations/fade.animations';
import { PreviewMode } from '../../model/preview-mode';
import { PeopleService } from '../services/people.service';
import { PreviewType } from '../../../results/models/view-filters';
import { PreviewPeopleResults } from '../models/preview-people-results';
import { firstValueFrom } from 'rxjs';
import { RouterService } from '@shared/services/router.service';

@UntilDestroy()
@Component({
  selector: 'people-preview',
  templateUrl: './people-preview.component.html',
  styleUrls: ['./people-preview.component.scss'],
  animations: [fade],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PeoplePreviewComponent implements OnInit {
  private _person: People.PersonContext;
  private windowSize$ = windowSizeObserver();
  readonly SMALL_SCREEN = 910;
  childComponentFocused: ComponentFocused = {};
  childrenList: number[] = [];
  scrollHeight: string;
  widthScreen: number;
  compIndex: number;
  results: PreviewPeopleResults;
  @Input() telemetrySearch: Partial<EventInfo>;
  get person(): People.PersonContext {
    return this._person;
  }
  @Input() set person(value: People.PersonContext) {
    this._person = value;
    this.setResults();
    this.model = this.person?.details?.source;
    setTimeout(() => {
      this.scrollHeight = `${this.calcScrollHeight()}px`;
      this.cdr.markForCheck();
    }, 50);
  }
  @Input() size: PreviewMode;
  @Input() personIndex: number;
  @Input() isLauncher?: boolean = false;
  private _smallWidthPreview: boolean = true;
  @Input() set smallWidthPreview(val: boolean) {
    this._smallWidthPreview = val;
    this.scrollHeight = `${this.calcScrollHeight()}px`;
    this.cdr.markForCheck();
  }
  @Input() extraSmallWidthPreview: boolean;
  get smallWidthPreview(): boolean {
    return this._smallWidthPreview;
  }
  @Input() isFavorite: boolean;
  @Output() closePopup: EventEmitter<void> = new EventEmitter();
  model: Search.ResultResourceItem;
  @ViewChild('people_preview_content') private previewContentElement: ElementRef;

  get previewPopupLarge() {
    return this.size === 'popup' && this.widthScreen > this.SMALL_SCREEN;
  }

  get hiddenHierarchy(): boolean {
    const details = this.person?.details;
    return !details?.employees?.total && !details?.managers?.length;
  }
  @Output() loaded = new EventEmitter<any>();
  @Input() windowMode: boolean;
  _previewType: PreviewType;
  get previewType(): PreviewType {
    return this._previewType;
  }
  @Input() set previewType(value: PreviewType) {
    this._previewType = value;
    this.setResults();
  }
  constructor(
    private peopleService: PeopleService,
    private eventsService: EventsService,
    private cdr: ChangeDetectorRef,
    private previewKeyboardService: PreviewKeyboardService,
    private hubService: HubService,
    private previewService: PreviewService,
    private routerService: RouterService
  ) {}

  ngOnInit(): void {
    this.windowSize$.pipe(untilDestroyed(this)).subscribe((res) => {
      setTimeout(() => {
        this.widthScreen = res.width;
        this.scrollHeight = `${this.calcScrollHeight()}px`;
        this.cdr.markForCheck();
      }, 0);
    });
    this.previewKeyboardService.componentFocused$.pipe(untilDestroyed(this)).subscribe((val) => {
      if (!val || val.compId !== PreviewFocus.Preview) return;
      this.compIndex = val.direction === 'first' ? 0 : this.childrenList.length - 1;
      this.childComponentFocused = { compIndex: this.childrenList[this.compIndex], direction: val.direction };
      this.cdr.markForCheck();
    });
    this.loaded.emit(this.person);
  }

  setResults() {
    if (this.previewType !== 'relevant-people' || !this.person) {
      return;
    }
    this.results = {
      items: this.person.contributions,
      totalResults: this.person.contributions?.length,
      paging: { next: null },
    };
    this.cdr.markForCheck();
  }

  async openPopup(item: HierarchyItem): Promise<void> {
    const person = await firstValueFrom(await this.peopleService.getPerson(item.id));
    if (!person) {
      return;
    }

    const model = {
      type: 'person',
      id: item.id,
      link: { id: person.details?.source?.link?.id },
      resource: { name: item.title, id: person.details?.source?.resource?.id, externalId: item.id },
      action: { type: 'people' },
    } as any;

    const currentUrl = this.routerService.current;

    //state for open aux people from aux
    if (currentUrl?.startsWith('/window')) {
      const url = this.previewService.getPreviewUrl(model, this.previewType, true);
      this.routerService.navigateByUrl(`/window/${url}`, { replaceUrl: true });
    } else {
      this.previewService.setPreviewState('popup', 0, model);
    }

    this.eventsService.event('preview.open_preview', {
      location: { title: this.hubService.currentLocation },
      resources: [
        {
          id: item.id,
          list: 'people_view',
          type: 'Person',
          position: item.index,
        },
      ],
    });
  }

  private calcScrollHeight(): number {
    if (this.size === 'popup' && !this.windowMode) {
      const marginInPopUp = this.widthScreen > this.SMALL_SCREEN ? 122 : 114;
      return this.peopleService.popupSize - marginInPopUp;
    }
    const contentElementHeight = window.innerHeight - this.previewContentElement?.nativeElement.getBoundingClientRect().top;
    if (!contentElementHeight) {
      return 0;
    }
    return contentElementHeight - 10;
  }

  existDetails() {
    return this.person?.details?.email || this.person?.details?.phone || this.person?.details?.location || this.person?.details?.department;
  }

  onClosePopup() {
    this.closePopup.emit();
  }

  onComponentFocusedExit(val: string) {
    if (val === 'prev') {
      if (--this.compIndex < 0) {
        this.previewKeyboardService.setComponentFocused('prev');
      }
      this.childComponentFocused = { compIndex: this.childrenList[this.compIndex], direction: 'last' };
    }

    if (val === 'next') {
      if (++this.compIndex >= this.childrenList.length) {
        this.previewKeyboardService.setComponentFocused('next');
      }
      this.childComponentFocused = { compIndex: this.childrenList[this.compIndex], direction: 'first' };
    }

    this.cdr.markForCheck();
  }

  addChild(compId) {
    if (!this.childrenList.includes(compId)) {
      this.childrenList.push(compId);
      this.childrenList.sort();
    }
  }
}
